<template>
  <div>
    <t-divider> </t-divider>
    <p style="margin: 14px 0">
      <text-with-mark :text="$t('用户意图')" />
    </p>
    <t-form-item name="desc" :rules="requireRule">
      <t-textarea
        v-model="formData.desc"
        :autosize="{ minRows: 6 }"
        maxlength="200"
        :placeholder="$t('帮助大模型更好理解用户意图，200 字以内')"
      />
    </t-form-item>
    <p style="margin: 14px 0">
      <text-with-mark
        :hidden-mark="true"
        :text="$t('举个例子，让大模型更好理解')"
      />
    </p>
    <t-table
      class="no-border-table"
      :data="formData.examples"
      row-key="id"
      drag-sort="row-handler"
      lazy-load
      size="small"
      :columns="columns"
      vertical-align="top"
      :show-header="false"
      @dragSort="onDragSort"
    >
      <template #example="{ rowIndex }">
        <t-form-item :name="`examples[${rowIndex}]`" :required-mark="false">
          <t-space :size="8" class="example-space">
            <t-input
              v-model="formData.examples[rowIndex].q"
              maxlength="200"
              style="width: 385px"
            />
            <CloseCircleIcon
              size="16"
              style="cursor: pointer"
              @click="deleteExamples(rowIndex)"
            />
          </t-space>
        </t-form-item>
      </template>
      <template #bottomContent>
        <t-form-item
          class="no-border-table-bottom"
          name="examples"
          :rules="examplesRules"
          :status-icon="false"
        >
          <div v-if="formData.examples.length < 20">
            <t-link theme="primary" hover="color" @click="addExamples">{{
              $t("添加例句")
            }}</t-link>
          </div>
        </t-form-item>
      </template>
    </t-table>
  </div>
</template>

<script lang="ts" setup>
import { reactive, watch } from "vue";
import { CloseCircleIcon, MoveIcon } from "tdesign-icons-vue-next";
import { toString, map, merge, cloneDeep, compact, trim } from "lodash-es";
import { t } from "@/utils/i18n";
import {
  Example,
  UserIntentNodeData,
  UserIntentFormData,
} from "@/types/userIntentNodeType";

const emit = defineEmits(["updateData"]);

const examplesRules = [
  {
    validator: (examples) => {
      const _arr = compact(examples);
      if (new Set(_arr).size !== _arr.length) {
        return false;
      }
      return true;
    },
    message: t("问法不能重复"),
    trigger: "change",
  },
];

const props = defineProps<{
  data: UserIntentNodeData;
}>();

const formData = reactive<UserIntentFormData>({
  examples: map(
    props.data.examples.length ? props.data.examples : [""],
    (v) => ({
      id: toString(Math.random()),
      q: v,
    }),
  ),
  desc: props.data.desc ?? "",
});

const onDragSort = (params: { currentData: Example[] }) => {
  formData.examples = cloneDeep(params.currentData);
};

const requireRule = [
  {
    validator: (str) => {
      return !!trim(str);
    },
    message: t("不能为空"),
    trigger: "change",
  },
];

const columns = [
  {
    colKey: "example",
  },
];

const addExamples = () => {
  formData.examples.push({
    id: toString(Math.random()),
    q: "",
  });
};

const deleteExamples = (index: number) => {
  formData.examples.splice(index, 1);
};

watch(
  formData,
  (newVal) => {
    emit(
      "updateData",
      merge({}, newVal, { examples: map(newVal.examples, "q") }),
    );
  },
  { deep: true },
);
</script>
<style lang="less" scoped>
:deep(.flow-t-table td) {
  border: none;
}
:deep(.flow-t-table.flow-t-size-s td) {
  padding: 0px;
}
:deep(.flow-t-table tr) {
  height: 52px;
}
:deep(.example-space) {
  display: flex;
  align-items: center;
}
:deep(.flow-t-form__controls.flow-t-is-error) {
  min-height: unset;
  .flow-t-form__controls-content {
    min-height: unset;
  }
}

.no-border-table-bottom {
  padding-bottom: 30px;
}
</style>
